import { useState } from 'react';
import { Network, Check, Users, Megaphone, LayoutDashboard, Sparkles, ChevronDown, ArrowRight, Target, Rocket } from 'lucide-react';

// Reemplaza estos marcadores por tus enlaces reales de Stripe/Hotmart.
const LINK_PAGO_CONSTRUCTOR_MENSUAL = 'https://buy.stripe.com/5kQ4gy9aMbSz1I53NWds81B';
const LINK_PAGO_CONSTRUCTOR_ANUAL = 'https://buy.stripe.com/dRm5kC4Uwg8P2M92JSds81C';
const WHATSAPP = 'https://wa.me/19548059212';

const FAQS = [
  { q: '¿Qué es el Constructor de Red?', a: 'Es una suscripción mensual que te permite registrar hasta 100 agentes en tu red sin pagar $197 por cada uno.' },
  { q: '¿Qué pasa si tengo más de 100 agentes?', a: 'Puedes sumar bloques de 50 agentes más por $10 al mes cada uno.' },
  { q: '¿Qué pasa con mis agentes si cancelo?', a: 'Tus agentes se mantienen activos mientras la suscripción esté activa. Si cancelas, cada agente puede pagar su propio ingreso de $197 para seguir activo o pasar a otra agencia de la red. Los agentes nuevos vuelven a costar $197.' },
  { q: '¿El Constructor de Red cambia mi porcentaje de comisión?', a: 'No. Tu porcentaje depende de tu plan de agencia: 50% Starter, 60% Profesional y 80% Travel Empire.' },
  { q: '¿Travel Empire incluye el Constructor de Red?', a: 'Sí, gratis y de por vida, con agentes ilimitados.' },
  { q: '¿Los planes de agencia tienen mensualidad?', a: 'No. Starter, Profesional y Travel Empire son de pago único y de por vida. El Constructor de Red es una herramienta opcional.' },
  { q: '¿Gano Bono de Registración sobre el pago de la suscripción?', a: 'No. Sobre el valor de la suscripción del Constructor de Red se gana exclusivamente el override. No existe Bono de Registración en el pago de la suscripción.' },
];

const FaqItem = ({ item, open, onToggle, idx }) => (
  <div className="border border-white/10 rounded-xl overflow-hidden bg-[#0d1526]">
    <button
      onClick={onToggle}
      data-testid={`constructor-faq-q-${idx}`}
      className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-white/5 transition-colors"
    >
      <span className="text-sm sm:text-base font-bold text-white">{item.q}</span>
      <ChevronDown className={`h-5 w-5 text-[#80C753] shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
    </button>
    {open && (
      <div className="px-5 pb-4 text-sm text-slate-300 leading-relaxed" data-testid={`constructor-faq-a-${idx}`}>
        {item.a}
      </div>
    )}
  </div>
);

export const NetworkBuilderSection = ({ promoMode = false }) => {
  const [ciclo, setCiclo] = useState('mensual'); // 'mensual' | 'anual'
  const [faqOpen, setFaqOpen] = useState(null);

  const precio = ciclo === 'mensual' ? '$59' : '$590';
  const periodo = ciclo === 'mensual' ? 'al mes' : 'al año';
  const linkConstructor = ciclo === 'mensual' ? LINK_PAGO_CONSTRUCTOR_MENSUAL : LINK_PAGO_CONSTRUCTOR_ANUAL;

  return (
    <section data-testid="herramientas-section" className="cv-fade-in-up mb-16">
      <div className="text-center mb-8">
        <span className="inline-flex items-center gap-2 bg-[#80C753]/15 border border-[#80C753]/40 rounded-full px-4 py-2 mb-4">
          <Sparkles className="h-4 w-4 text-[#80C753]" />
          <span className="text-[#80C753] text-xs font-black uppercase tracking-[0.2em]">Herramientas</span>
        </span>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight" style={{ fontFamily: 'Poppins, sans-serif' }}>
          Herramientas para hacer crecer tu agencia
        </h2>
        <p className="text-slate-300 text-base sm:text-lg mt-3">Activa solo lo que necesitas, cuando lo necesitas.</p>
      </div>

      <div className={`grid grid-cols-1 ${promoMode ? 'md:grid-cols-2' : 'lg:grid-cols-3'} gap-6 items-stretch max-w-6xl mx-auto`}>
        {/* Tarjeta 1 — Constructor de Red (destacada) */}
        <div
          data-testid="constructor-red-card"
          className="relative lg:col-span-1 rounded-3xl p-[2px] bg-gradient-to-br from-[#80C753] via-[#45A0EE] to-[#80C753] shadow-[0_0_60px_rgba(128,199,83,0.25)]"
        >
          <div className="relative h-full rounded-3xl bg-gradient-to-br from-[#06271a] via-[#00153F] to-[#06271a] p-7 flex flex-col overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(500px_260px_at_50%_-10%,rgba(128,199,83,.22),transparent_60%)] pointer-events-none" />
            <div className="relative flex flex-col h-full">
              <div className="flex items-center justify-between mb-4">
                <div className="w-14 h-14 rounded-2xl bg-[#80C753] flex items-center justify-center">
                  <Network className="h-7 w-7 text-[#00153F]" />
                </div>
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#00153F] bg-[#80C753] rounded-full px-3 py-1 animate-pulse">Nuevo</span>
              </div>

              <h3 className="text-2xl font-black text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>Constructor de Red</h3>
              <p className="text-[#80C753] font-bold text-sm mt-1">La herramienta más espectacular para multiplicar tu agencia.</p>

              {/* Selector Mensual / Anual */}
              <div className="mt-5 inline-flex self-start rounded-full bg-black/30 border border-white/10 p-1" data-testid="constructor-ciclo-toggle">
                <button
                  onClick={() => setCiclo('mensual')}
                  data-testid="constructor-ciclo-mensual"
                  className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${ciclo === 'mensual' ? 'bg-[#80C753] text-[#00153F]' : 'text-slate-300'}`}
                >
                  Mensual
                </button>
                <button
                  onClick={() => setCiclo('anual')}
                  data-testid="constructor-ciclo-anual"
                  className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${ciclo === 'anual' ? 'bg-[#80C753] text-[#00153F]' : 'text-slate-300'}`}
                >
                  Anual
                </button>
              </div>

              <div className="mt-4 flex items-end gap-2 flex-wrap">
                {promoMode ? (
                  <>
                    <span className="text-slate-400 line-through text-2xl font-black mb-1" data-testid="constructor-precio">{precio}</span>
                    <span className="text-5xl font-black text-[#80C753]">$0</span>
                    <span className="text-slate-200 text-sm font-semibold mb-1.5">por 1 año</span>
                  </>
                ) : (
                  <>
                    <span className="text-5xl font-black text-white" data-testid="constructor-precio">{precio}</span>
                    <span className="text-slate-300 text-sm font-semibold mb-1.5">{periodo}</span>
                  </>
                )}
              </div>
              {promoMode ? (
                <p className="text-[#E3C766] text-xs font-bold mt-1">Luego {precio} {periodo}. Hoy GRATIS con el plan Profesional.</p>
              ) : ciclo === 'anual' && (
                <p className="text-[#E3C766] text-xs font-bold mt-1">2 meses gratis vs. el plan mensual.</p>
              )}

              <p className="text-white font-bold text-base mt-5">
                Registra hasta 100 agentes en tu red sin pagar $197 por cada uno.
              </p>

              <ul className="mt-4 space-y-3 flex-1">
                {[
                  'Hasta 100 agentes activos en tu red',
                  'Cada agente gana el 25% en sus ventas y tú ganas la diferencia de tu nivel',
                  'Cancela cuando quieras',
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-sm text-slate-200">
                    <Check className="h-5 w-5 text-[#80C753] shrink-0 mt-0.5" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <p className="text-[11px] text-slate-400 mt-5">
                Disponible para agencias Starter y Profesional. Incluido gratis en Travel Empire.
              </p>

              <a
                href={linkConstructor}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="constructor-cta"
                className="mt-4 inline-flex items-center justify-center gap-2 w-full rounded-full bg-[#80C753] hover:bg-[#6fb045] text-[#00153F] font-black text-base py-4 transition-colors"
              >
                Activar Constructor de Red
                <ArrowRight className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>

        {/* Tarjeta 2 — Leads */}
        <div data-testid="leads-card" className="rounded-3xl border border-white/10 bg-[#0d1526] p-7 flex flex-col">
          <div className="w-14 h-14 rounded-2xl bg-[#45A0EE] flex items-center justify-center mb-4">
            <Megaphone className="h-7 w-7 text-white" />
          </div>
          <h3 className="text-2xl font-black text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>Leads de Club Viajemos</h3>
          <div className="mt-3 flex items-end gap-2">
            <span className="text-slate-300 text-sm font-semibold mb-1">desde</span>
            <span className="text-4xl font-black text-white">$0.99</span>
            <span className="text-slate-300 text-sm font-semibold mb-1.5">por lead</span>
          </div>
          <p className="text-slate-200 text-sm mt-4 flex-1">
            Clientes potenciales de nuestras campañas en Meta, para que tú te dediques a cerrar ventas.
          </p>
          <p className="text-[12px] text-[#80C753] font-bold mt-4 leading-snug" data-testid="leads-primeros-100">🎁 Tus primeros 100 CLIENTES van por nuestra cuenta al registrarte como agencia.</p>
          <a
            href={`${WHATSAPP}?text=${encodeURIComponent('Hola, quiero pedir leads de Club Viajemos para mi agencia.')}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="leads-cta"
            className="mt-4 inline-flex items-center justify-center gap-2 w-full rounded-full bg-[#25D366] hover:bg-[#1eb959] text-white font-black text-base py-4 transition-colors"
          >
            Pedir leads
            <ArrowRight className="h-5 w-5" />
          </a>
        </div>

        {/* Tarjeta 3 — Motor de Ventas Viajemos (CRM) */}
        <div data-testid="command-center-card" className="rounded-3xl border border-white/10 bg-[#0d1526] p-7 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <div className="w-14 h-14 rounded-2xl bg-[#00153F] border border-[#45A0EE]/40 flex items-center justify-center">
              <LayoutDashboard className="h-7 w-7 text-[#45A0EE]" />
            </div>
            <span className="text-[10px] font-black uppercase tracking-[0.15em] text-[#45A0EE] bg-[#45A0EE]/10 border border-[#45A0EE]/30 rounded-full px-3 py-1">Incluido en todos los planes</span>
          </div>
          <h3 className="text-2xl font-black text-white leading-tight" style={{ fontFamily: 'Poppins, sans-serif' }}>Motor de Ventas Viajemos (CRM)</h3>
          <p className="text-[#45A0EE] font-bold text-sm mt-1">El corazón de tu agencia: convierte todas tus herramientas en ventas, en piloto automático.</p>

          <p className="text-slate-300 text-sm mt-4 leading-relaxed">
            Nuestro motor de búsqueda y ventas trabaja por ti: encuentra y califica a tus clientes, los contacta por el canal correcto y los acompaña hasta la compra. Diseñado para que cada agente venda más, sin perder ni un solo prospecto.
          </p>

          <ul className="mt-4 space-y-3 flex-1">
            {[
              'IA que responde, califica y agenda a tus prospectos al instante',
              'WhatsApp, email y SMS automáticos conectados en un solo lugar',
              'Recordatorios y seguimientos que salen solos hasta cerrar la venta',
              'Funnels y páginas de captura listos para traer clientes nuevos',
              'Atrae clientes y los atiende al instante, de día y de noche',
              'Activa tus certificados, tu plataforma y tus asesorías para vender más',
              'Embudos, citas y mensajes automáticos trabajando por ti 24/7',
            ].map((b) => (
              <li key={b} className="flex items-start gap-2.5 text-sm text-slate-200">
                <Check className="h-5 w-5 text-[#45A0EE] shrink-0 mt-0.5" />
                <span>{b}</span>
              </li>
            ))}
          </ul>

          <div className="mt-5 rounded-2xl border border-[#80C753]/30 bg-[#80C753]/10 p-4">
            <div className="flex items-center gap-2 mb-1">
              <Target className="h-4 w-4 text-[#80C753]" />
              <span className="text-[10px] font-black uppercase tracking-[0.18em] text-[#80C753]">El objetivo</span>
            </div>
            <p className="text-sm text-white font-bold leading-snug">Convertir ventas todos los días — de forma constante y automática.</p>
          </div>

          <div className="mt-4 inline-flex items-center gap-2 text-[#80C753] font-bold text-sm">
            <Users className="h-5 w-5" /> Ya viene activo contigo
          </div>
        </div>

        {/* Tarjeta 4 — Training de Ventas 30 días (solo /promo) */}
        {promoMode && (
          <div
            data-testid="training-tool-card"
            className="relative rounded-3xl p-[2px] overflow-hidden"
            style={{ background: 'linear-gradient(135deg,#E3C766,#fff2c2,#E3C766)' }}
          >
            <div className="relative h-full rounded-3xl bg-gradient-to-br from-[#120f06] via-[#1a1508] to-[#0f1320] p-7 flex flex-col overflow-hidden">
              <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-[#E3C766]/20 blur-3xl pointer-events-none" />
              <div className="relative flex flex-col h-full">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#E3C766] to-[#caa23a] flex items-center justify-center">
                    <Rocket className="h-7 w-7 text-[#120f06]" />
                  </div>
                  <span className="text-[10px] font-black uppercase tracking-[0.15em] text-[#120f06] bg-[#E3C766] rounded-full px-3 py-1">Bonus de hoy · Gratis</span>
                </div>
                <h3 className="text-2xl font-black text-white leading-tight" style={{ fontFamily: 'Poppins, sans-serif' }}>El despegue de tu agencia 🚀</h3>
                <p className="text-[#E3C766] font-bold text-sm mt-1">Training de Ventas de 30 días con el equipo ganador de Club Viajemos.</p>

                <p className="text-slate-300 text-sm mt-4 leading-relaxed">
                  Durante 30 días entrenas al lado de quienes ya lo lograron. Aprendes vendiendo de verdad, con el acompañamiento del equipo que más vende — para que arranques bien desde el primer día.
                </p>

                <ul className="mt-4 space-y-3 flex-1">
                  {[
                    '30 días de entrenamiento en vivo con el equipo ganador',
                    'Aprendes haciendo: práctica real de ventas, no solo teoría',
                    'Acompañamiento para cerrar tus primeras ventas',
                    'El atajo que te ahorra años de prueba y error',
                  ].map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-sm text-slate-200">
                      <Check className="h-5 w-5 text-[#E3C766] shrink-0 mt-0.5" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-5 rounded-2xl border border-[#E3C766]/30 bg-[#E3C766]/10 p-4">
                  <p className="text-sm text-white font-black italic">No hay mejor comienzo.</p>
                </div>

                <a
                  href="/training"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="training-tool-link"
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-xl py-3.5 font-black uppercase tracking-wide text-[#120f06] transition-transform hover:-translate-y-0.5"
                  style={{ background: 'linear-gradient(135deg,#E3C766,#fff2c2,#E3C766)' }}
                >
                  Ver el programa <ArrowRight className="h-5 w-5" />
                </a>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* FAQ Constructor de Red */}
      <div className="max-w-3xl mx-auto mt-14">
        <h3 className="text-2xl sm:text-3xl font-black text-white text-center mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
          Preguntas frecuentes
        </h3>
        <div className="space-y-3" data-testid="constructor-faq">
          {FAQS.map((item, idx) => (
            <FaqItem key={idx} idx={idx} item={item} open={faqOpen === idx} onToggle={() => setFaqOpen(faqOpen === idx ? null : idx)} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default NetworkBuilderSection;
